/**
 * Security · Paths — leads with the mode switch + a table of the
 * TOP MATCHED PATHS the Worker is actually seeing, each tagged with
 * its probe kind and current refused/counted status.
 *
 * Before (2026-10-20): the tab mounted the raw ProtectedPathsCard,
 * which buried the merchant's "which paths?" question under the Turn
 * on/off button, counter cards, custom-paths editor and the long
 * built-in categories list.
 *
 * After: the mode strip + the explainer stay at the top (nothing a
 * merchant sees first should be about counting), followed by the
 * Path · Kind · Hits · Status table. Custom paths / allow exceptions
 * / the built-in list all collapse into one "Edit path list"
 * disclosure. Stacks on 390px.
 */

import { useCallback, useEffect, useState } from 'react';

import type { Site } from '@sill/types';
import type { ProtectedPathsResponse } from '@sill/types/api';

import { useOptionalAuth } from '../../app/auth-context.js';
import { useOptionalHttp } from '../../api/http-context.js';
import { getProtectedPaths, putProtectedPathsMode } from '../../api/threats.js';
import { Card, Collapsible, ConfirmDialog } from '../../components/atoms/index.js';
import { useC } from '../../lib/theme.js';
import { ProtectedPathsListEditor } from '../settings/SettingsInstallBlocking.js';
import { probeKindOf } from './probe-kind.js';
import { INSTALL_HREF, pathsNoServerCopy, serverInstallOf } from './server-install.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export interface PathsViewProps {
  readonly site: Site;
}

interface Row {
  readonly path: string;
  readonly kind: string;
  readonly hits: number;
}

export function PathsView({ site }: PathsViewProps): JSX.Element | null {
  const C = useC();
  const http = useOptionalHttp();
  const auth = useOptionalAuth();
  const role = auth !== null && auth.state.kind === 'authenticated'
    ? auth.state.user.role : null;

  const [state, setState] = useState<ProtectedPathsResponse | null>(null);
  const [confirmFlip, setConfirmFlip] = useState<'observe' | 'enforce' | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    if (http === null) return;
    try {
      const res = await getProtectedPaths(http, site.site_id);
      setState(res);
    } catch { /* keep null; the view hides until the server answers */ }
  }, [http, site.site_id]);

  useEffect(() => { void refetch(); }, [refetch]);

  const flipMode = useCallback(async (next: 'observe' | 'enforce'): Promise<void> => {
    if (http === null || state === null) return;
    setPending('mode');
    setError(null);
    const r = await putProtectedPathsMode(http, site.site_id, next);
    if (!r.ok) {
      if (r.error === 'install_cannot_enforce') {
        setError(r.install_version === null || r.install_version === undefined
          ? `Sill has not seen your install version yet. It appears after your Worker or WordPress plugin next reports in (version ${r.min_enforcing_worker_version} or later can refuse requests).`
          : `Update your Sill Worker or WordPress plugin to ${r.min_enforcing_worker_version} or later to refuse requests. Your current version is ${r.install_version}.`);
      } else if (r.error === 'forbidden') {
        setError('You do not have permission to change this setting.');
      } else {
        setError('That change was rejected. Try again.');
      }
    } else {
      await refetch();
    }
    setPending(null);
    setConfirmFlip(null);
  }, [http, refetch, site.site_id, state]);

  if (state === null) {
    return (
      <div data-testid="paths-loading" style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1, padding: '14px 16px' }}>
        LOADING…
      </div>
    );
  }

  const canMutate = state.may_mutate && (role === 'owner' || role === 'admin');
  const window_days = state.window_days;
  const refused = state.refused_count_window;
  const would = state.would_refuse_count_window;
  const rows: readonly Row[] = state.top_paths.map((p) => ({
    path: p.path,
    kind: probeKindOf(p.path),
    hits: p.count,
  }));
  const refusing = state.mode === 'enforce';
  // Founder 2026-10-29: with no Worker or plugin, nothing can count or
  // refuse these requests. Say so instead of a zero count and a switch
  // that cannot work.
  const install = serverInstallOf(site, { install_version: state.install_version });
  const noServer = install !== 'server' && !refusing;

  const statusLabel = refusing ? 'Refused' : 'Counted';
  const statusColor = refusing ? C.coralFg : C.muted;

  return (
    <div data-testid="paths-view">
      {/* Mode strip + Turn on/off. Lives first — this is the single
          control that defines whether the page is on or off. */}
      <Card data-testid="paths-mode-card">
        <div style={{ padding: '18px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <h3 style={{ fontFamily: sansFont, fontSize: 15, fontWeight: 600, color: C.text, margin: 0 }}>
              Paths
            </h3>
            <span
              data-testid="paths-mode-indicator"
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                letterSpacing: 2,
                color: C.muted,
                textTransform: 'uppercase',
              }}
            >
              {refusing
                ? 'ON · REFUSING'
                : install === 'shopify_none'
                  ? 'HANDLED BY SHOPIFY'
                  : install === 'script_none'
                    ? 'NEEDS A SERVER INSTALL'
                    : 'OFF · COUNTING ONLY'}
            </span>
          </div>
          {/* Explainer sentence — stays verbatim from the original card,
              so the merchant still knows this refuses attack paths only
              and does not block any address. */}
          <div
            data-testid="paths-explainer"
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.body,
              lineHeight: 1.55,
              marginTop: 6,
            }}
          >
            Refuses requests for <strong>attack paths only</strong> (environment files, secret keys, backups, webshells, debug pages), from anyone, with a plain &ldquo;not found&rdquo;. No address is blocked: the same visitor can still open every normal page.
          </div>
          {noServer ? (
            <div data-testid="paths-no-server" style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.55, marginTop: 6 }}>
              {pathsNoServerCopy(install)}
              {install === 'script_none' ? (
                <>
                  {' '}
                  <a href={INSTALL_HREF} data-testid="paths-install-link" style={{ color: C.text, textDecoration: 'underline' }}>
                    Open Settings › Install
                  </a>
                </>
              ) : null}
            </div>
          ) : null}
          {noServer ? null : (
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, marginTop: 6 }}>
            {refusing
              ? `On: these requests are refused by Sill's Worker and never reach your server. ${refused.toLocaleString('en-US')} refused in the last ${window_days} days.`
              : `Off: these requests still reach your server. In the last ${window_days} days, ${would.toLocaleString('en-US')} would have been refused.`}
          </div>
          )}
          {canMutate && !noServer ? (
            <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {refusing ? (
                <button
                  type="button"
                  data-testid="paths-flip-observe"
                  disabled={pending !== null}
                  onClick={() => setConfirmFlip('observe')}
                  style={{
                    background: 'transparent',
                    color: C.text,
                    border: `1px solid ${C.border}`,
                    borderRadius: 2,
                    padding: '8px 14px',
                    fontFamily: sansFont,
                    fontSize: 13,
                    cursor: pending === null ? 'pointer' : 'default',
                  }}
                >
                  Turn off attack paths
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="paths-flip-enforce"
                  disabled={pending !== null || !state.install_can_enforce}
                  onClick={() => setConfirmFlip('enforce')}
                  style={{
                    background: state.install_can_enforce ? C.lime : 'transparent',
                    color: state.install_can_enforce ? C.ink : C.muted,
                    border: `1px solid ${C.border}`,
                    borderRadius: 2,
                    padding: '8px 14px',
                    fontFamily: sansFont,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: (pending === null && state.install_can_enforce) ? 'pointer' : 'default',
                  }}
                >
                  Turn on attack paths
                </button>
              )}
              {!refusing && !state.install_can_enforce ? (
                <span
                  data-testid="paths-install-gate"
                  style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}
                >
                  {state.install_version === null || state.install_version === undefined
                    ? `Sill has not seen your install version yet. It appears after your Worker or WordPress plugin next reports in (version ${state.min_enforcing_worker_version} or later can refuse requests).`
                    : `Update your Sill Worker or WordPress plugin to ${state.min_enforcing_worker_version} or later to refuse requests. Your current version is ${state.install_version}.`}
                </span>
              ) : null}
            </div>
          ) : null}
          {error !== null ? (
            <div
              data-testid="paths-error"
              style={{
                marginTop: 10,
                padding: '8px 12px',
                border: `1px solid ${C.coral}`,
                borderRadius: 2,
                fontFamily: sansFont,
                fontSize: 12,
                color: C.text,
              }}
            >
              {error}
            </div>
          ) : null}
        </div>
      </Card>

      {/* Paths table — leads the view with "which paths?". Hidden with no
          Worker or plugin: nothing can see these requests, so an empty
          table would read as "none happened". */}
      {noServer ? null : (
      <div style={{ marginTop: 16 }}>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            letterSpacing: 2,
            color: C.muted,
            textTransform: 'uppercase',
            marginBottom: 8,
          }}
        >
          Top matched paths &middot; {rows.length}
        </div>
        {rows.length === 0 ? (
          <Card>
            <div
              data-testid="paths-empty"
              style={{ padding: '14px 16px', fontFamily: sansFont, fontSize: 13, color: C.muted }}
            >
              No attack paths have been requested in the last {window_days} days.
            </div>
          </Card>
        ) : (
          <Card>
            {/* Desktop / tablet: 4-column grid header + rows. */}
            <div className="hidden sm:block" data-testid="paths-table">
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) 140px 100px 110px',
                  gap: 10,
                  padding: '12px 16px',
                  borderBottom: `1px solid ${C.borderSoft}`,
                  fontFamily: monoFont,
                  fontSize: 9,
                  letterSpacing: 1.5,
                  color: C.muted,
                  textTransform: 'uppercase',
                }}
              >
                <span>Path</span>
                <span>Kind</span>
                <span style={{ textAlign: 'right' }}>Hits</span>
                <span style={{ textAlign: 'right' }}>Status</span>
              </div>
              {rows.map((r) => (
                <div
                  key={r.path}
                  data-testid="paths-row"
                  data-path={r.path}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) 140px 100px 110px',
                    gap: 10,
                    padding: '10px 16px',
                    borderBottom: `1px solid ${C.borderSoft}`,
                    alignItems: 'center',
                  }}
                >
                  <span style={{ fontFamily: monoFont, fontSize: 12, color: C.text, overflowWrap: 'anywhere' }}>
                    {r.path}
                  </span>
                  <span style={{ fontFamily: sansFont, fontSize: 13, color: C.body }}>
                    {r.kind}
                  </span>
                  <span style={{ fontFamily: monoFont, fontSize: 12, color: C.text, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {r.hits.toLocaleString('en-US')}
                  </span>
                  <span
                    data-testid="paths-row-status"
                    style={{
                      fontFamily: monoFont,
                      fontSize: 10,
                      letterSpacing: 1,
                      color: statusColor,
                      textAlign: 'right',
                      textTransform: 'uppercase',
                    }}
                  >
                    {statusLabel}
                  </span>
                </div>
              ))}
            </div>
            {/* 390px: stack path on line 1, "Kind · N hits · Status" on line 2. */}
            <div className="sm:hidden" data-testid="paths-table-mobile">
              {rows.map((r) => (
                <div
                  key={r.path}
                  data-testid="paths-row-mobile"
                  data-path={r.path}
                  style={{
                    padding: '10px 14px',
                    borderBottom: `1px solid ${C.borderSoft}`,
                  }}
                >
                  <div style={{ fontFamily: monoFont, fontSize: 12, color: C.text, overflowWrap: 'anywhere' }}>
                    {r.path}
                  </div>
                  <div
                    style={{
                      fontFamily: monoFont,
                      fontSize: 10,
                      letterSpacing: 0.5,
                      color: C.muted,
                      marginTop: 4,
                    }}
                  >
                    {r.kind} &middot; {r.hits.toLocaleString('en-US')} {r.hits === 1 ? 'hit' : 'hits'} &middot;{' '}
                    <span style={{ color: statusColor, textTransform: 'uppercase', letterSpacing: 1 }}>{statusLabel}</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
      )}

      {/* Edit path list — custom paths + allow-exceptions editors + the
          built-in categories list. Collapsed by default so the primary
          landing is the table above. */}
      {install === 'shopify_none' && !refusing ? null : (
      <div style={{ marginTop: 16 }} data-testid="paths-edit-disclosure-wrap">
        <Collapsible label="Edit path list">
          <div data-testid="paths-edit-disclosure">
            <ProtectedPathsListEditor
              kind="custom"
              site_id={site.site_id}
              http={http}
              canMutate={canMutate}
              cap={state.caps.custom_paths}
              maxLength={state.caps.pattern_max_length}
              entries={state.custom_paths}
              onSaved={refetch}
            />
            <ProtectedPathsListEditor
              kind="exceptions"
              site_id={site.site_id}
              http={http}
              canMutate={canMutate}
              cap={state.caps.exceptions}
              maxLength={state.caps.pattern_max_length}
              entries={state.exceptions}
              onSaved={refetch}
            />
            {/* Built-in categories list — informational, read-only. */}
            <div style={{ marginTop: 20 }} data-testid="paths-categories">
              <div
                style={{
                  fontFamily: monoFont,
                  fontSize: 9,
                  letterSpacing: 2,
                  color: C.muted,
                  textTransform: 'uppercase',
                  marginBottom: 6,
                }}
              >
                Built-in list &middot; {state.categories.length} &middot; v{state.list_version}
              </div>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {state.categories.map((row) => (
                  <li
                    key={row.label}
                    style={{ padding: '8px 0', borderBottom: `1px solid ${C.borderSoft}` }}
                  >
                    <div
                      style={{
                        fontFamily: monoFont,
                        fontSize: 12,
                        color: C.text,
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {row.label}
                    </div>
                    <div
                      style={{
                        fontFamily: sansFont,
                        fontSize: 12,
                        color: C.muted,
                        marginTop: 2,
                      }}
                    >
                      {row.description}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Collapsible>
      </div>
      )}

      <ConfirmDialog
        open={confirmFlip === 'enforce'}
        onClose={() => setConfirmFlip(null)}
        title={`Block attack paths on ${site.domain}?`}
        confirmLabel="Turn on attack paths"
        cancelLabel="Not now"
        confirmTone="lime"
        onConfirm={() => flipMode('enforce')}
        body={(
          <div>
            <p style={{ fontFamily: sansFont, fontSize: 13, color: C.text, margin: '0 0 8px' }}>
              Requests for the attack paths on this list will get a plain &ldquo;not found&rdquo; from Sill&apos;s Worker instead of reaching your server. In the last {window_days} days this would have stopped {would.toLocaleString('en-US')} request{would === 1 ? '' : 's'}.
            </p>
            <p style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, margin: 0 }}>
              Normal pages are not affected. You can turn it off again with one click.
            </p>
          </div>
        )}
      />
      <ConfirmDialog
        open={confirmFlip === 'observe'}
        onClose={() => setConfirmFlip(null)}
        title={`Turn off attack paths on ${site.domain}?`}
        confirmLabel="Turn off attack paths"
        cancelLabel="Keep them on"
        confirmTone="amber"
        onConfirm={() => flipMode('observe')}
        body={(
          <div>
            <p style={{ fontFamily: sansFont, fontSize: 13, color: C.text, margin: '0 0 8px' }}>
              Requests for these attack paths will reach your server again. Sill keeps counting them, and you can turn blocking back on any time.
            </p>
          </div>
        )}
      />
    </div>
  );
}
